import { useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface PickerRow {
  ref: string;
  name: string;
}

/**
 * Сохранённая в нашей базе ссылка 1С → значение пикера.
 *
 * Имя лежит в базе рядом со ссылкой, чтобы страница открывалась без похода в
 * 1С. Если имени нет (ссылку заводили руками или скриптом) — показываем сам
 * GUID: поле остаётся ЗАПОЛНЕННЫМ, а не выглядит пустым, и боевая ссылка не
 * теряется из-за того, что пикер не подгружал список.
 */
export function savedPickerValue(
  ref: string | null | undefined,
  name: string | null | undefined,
): PickerRow | null {
  const cleanRef = (ref ?? "").trim();
  if (!cleanRef) return null;
  return { ref: cleanRef, name: (name ?? "").trim() || cleanRef };
}

interface Props<T extends PickerRow = PickerRow> {
  value: T | null;
  onChange: (row: T | null) => void;
  search: (q: string) => Promise<T[]>;
  queryKey: ReadonlyArray<unknown>;
  placeholder: string;
  disabled?: boolean;
}

export function RemotePicker<T extends PickerRow = PickerRow>({
  value,
  onChange,
  search,
  queryKey,
  placeholder,
  disabled,
}: Props<T>): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const { data: rows = [], isFetching } = useQuery({
    queryKey: [...queryKey, debouncedQ],
    queryFn: () => search(debouncedQ),
    enabled: open && !disabled,
    staleTime: 30_000,
  });

  return (
    <div ref={rootRef} className="relative">
      {value && !open ? (
        // Высота как у Input (h-10) — чтобы поле с выбранным значением не было
        // выше соседних полей/селектов в одной строке.
        <div className="flex h-10 items-center gap-1 rounded-md border border-input bg-background px-3 text-sm">
          <span className="min-w-0 flex-1 truncate">{value.name}</span>
          <button
            type="button"
            onClick={() => onChange(null)}
            disabled={disabled}
            aria-label="Очистить"
            className="shrink-0 rounded p-1 text-muted-foreground hover:bg-accent disabled:opacity-50"
          >
            <X className="size-4" />
          </button>
          <button
            type="button"
            onClick={() => setOpen(true)}
            disabled={disabled}
            aria-label="Изменить"
            className="shrink-0 rounded p-1 text-muted-foreground hover:bg-accent disabled:opacity-50"
          >
            <ChevronDown className="size-4" />
          </button>
        </div>
      ) : (
        <Input
          placeholder={placeholder}
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          disabled={disabled}
        />
      )}

      {open && !disabled && (
        <ul className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-md border bg-card shadow-md">
          {isFetching && (
            <li className="px-3 py-2 text-sm text-muted-foreground">Загрузка…</li>
          )}
          {!isFetching && rows.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted-foreground">Ничего не найдено</li>
          )}
          {rows.map((r) => (
            <li key={r.ref}>
              <button
                type="button"
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent",
                  value?.ref === r.ref && "bg-accent",
                )}
                onClick={() => {
                  onChange(r);
                  setOpen(false);
                  setQ("");
                }}
              >
                {value?.ref === r.ref && <Check className="size-4" />}
                <span className="truncate">{r.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
